import React, { useState } from "react";
import { uploadChunked as uploadChunkedLib } from "../lib/upload";
import { useRecorder } from "../lib/useRecorder";
import { toast } from "./ui/sonner";
import { Loader2, Mic, Square, Sparkles } from "lucide-react";

async function uploadChunked(api, blob, name, type) {
  return (await uploadChunkedLib(api, blob, { name, type })).fileId;
}

// 语音巡检填报：录音 → Whisper 转写 → AI 抽取进度/质量/安全 → 回填表单（PM 核对后提交）
export const VoiceFill = ({ api, pid, tpl, zh, onFill }) => {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const process = async (blob) => {
    if (!blob.size) return;
    setBusy(true);
    try {
      const ext = blob.type.includes("mp4") ? "mp4" : "webm";
      const fileId = await uploadChunked(api, blob, `inspection_voice.${ext}`, blob.type.split(";")[0] || "audio/webm");
      const { data } = await api.post(`/projects/${pid}/site-inspections/voice`, { fileId, template: { trades: tpl.trades, quality: tpl.quality, safety: tpl.safety } });
      setLast(data);
      onFill(data);
      toast.success(zh ? `已根据语音填写 ${data.filled} 项，请核对后提交` : `Filled ${data.filled} field(s) from voice — please review`);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "语音处理失败" : "Voice processing failed")); }
    setBusy(false);
  };
  const { rec, sec, start, stop } = useRecorder({ maxSec: 120, onBlob: process });
  const begin = async () => {
    const err = await start();
    if (err === "unsupported") toast.error(zh ? "当前浏览器不支持录音，请用 Chrome / Safari 最新版" : "Recording not supported");
    else if (err) toast.error(zh ? "无法访问麦克风，请允许浏览器使用麦克风" : "Microphone access denied");
  };

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-3" data-testid="si-voice" data-state={rec ? "recording" : busy ? "processing" : "idle"}>
      <div className="flex items-center gap-2 flex-wrap">
        {!rec ? (
          <button type="button" data-testid="si-voice-start" disabled={busy} onClick={begin} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}{busy ? (zh ? "转写并填表中…" : "Transcribing…") : (zh ? "语音填报" : "Voice fill")}
          </button>
        ) : (
          <button type="button" data-testid="si-voice-stop" onClick={stop} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full bg-red-600 text-white animate-pulse">
            <Square className="h-3.5 w-3.5" />{zh ? "停止" : "Stop"} {String(Math.floor(sec / 60)).padStart(1, "0")}:{String(sec % 60).padStart(2, "0")}
          </button>
        )}
        <p className="text-[11px] text-violet-800 flex-1 min-w-[200px]">
          <Sparkles className="h-3 w-3 inline mr-1" />
          {zh ? "按下后直接口述：“Framing 分包到了 5 个人，二层框架做到 60%；电工接线盒没固定好需整改；脚手架护栏缺一段。” AI 会自动填到工序进度 / 质量 / 安全项，请核对后提交。" : "Speak naturally — AI fills progress / quality / safety items for your review."}
        </p>
      </div>
      {last && (
        <div className="mt-2 text-[11px] text-slate-700 bg-white rounded-lg border border-violet-100 p-2" data-testid="si-voice-transcript">
          <p className="font-semibold text-violet-700 mb-0.5">{zh ? "转写内容" : "Transcript"}{!last.ai && <span className="ml-2 text-amber-600 font-normal">{zh ? "（AI 抽取不可用，已填入备注）" : "(AI extraction unavailable)"}</span>}</p>
          <p className="whitespace-pre-wrap">{last.transcript}</p>
        </div>
      )}
    </div>
  );
};

// 把 AI 抽取结果合并进巡检表单：工序按名称匹配（不区分大小写），检查项按 key；其余文字进备注
export const mergeVoiceFill = (form, data) => {
  const norm = (s) => String(s || "").toLowerCase().replace(/[\s_-]/g, "");
  let progress = [...form.progress];
  for (const p of data.progress || []) {
    const i = progress.findIndex((r) => (p.trade && norm(r.trade) && (norm(r.trade).includes(norm(p.trade)) || norm(p.trade).includes(norm(r.trade)))) || (p.sub && norm(r.sub) && (norm(r.sub).includes(norm(p.sub)) || norm(p.sub).includes(norm(r.sub)))));
    if (i >= 0) progress[i] = { ...progress[i], pct: p.pct ?? progress[i].pct, crew: p.crew || progress[i].crew, note: p.note || progress[i].note };
    else progress = [...progress.filter((r) => r.trade || r.sub), { trade: p.trade, sub: p.sub || "", pct: p.pct || 0, crew: p.crew || 0, note: p.note || "" }];
  }
  const apply = (items, upd) => items.map((x) => { const u = (upd || []).find((y) => y.key === x.key); return u ? { ...x, result: u.result, note: u.note || x.note } : x; });
  const note = [form.note, data.note].filter(Boolean).join("\n");
  return { ...form, progress, quality: apply(form.quality, data.quality), safety: apply(form.safety, data.safety), note };
};
